import { z } from 'zod'
import { analyticsSearchSchema } from './analytics-search-schema'
import { LIST_TABS, SELECTION_VIEW_TABS } from './forecast-list-tabs'
import {
  PLANNER_GROUPS,
  SCHEDULED_GROUPS,
  UNSCHEDULED_GROUPS,
} from './forecast-queue-groups'
import { UNSCHEDULED_REASON_LABELS } from './forecast-queue-format'
import { FORECAST_STATUS_PILLS } from './forecast-status-pills'
import { FORECAST_GOALS } from './forecast-goals'
import { CASE_DESIGN_STAGES } from './forecast-stage-pills'
import { CLEANING_SINCE_ALL, isCleaningSince } from './cleaning-since'
import type { ForecastListTabValue } from './forecast-list-tabs'
import type { SelectionViewTab } from './forecast-selection-view'
import type { UnscheduledReason } from '@/types/backofficeAnalytics.types'
import {
  CASE_WORK_KINDS,
  CLEANING_FINDING_KINDS,
} from '@/types/backofficeAnalytics.types'
import { parseCsvParam, serializeCsvParam } from '@/lib/csv-search-param'

/**
 * Search params for the Forecasts sub-route (`analytics/forecasts/index.tsx`)
 * (sc-1212): the console's own params PLUS the page's view state, so a view is
 * shareable, bookmarkable and survives a refresh.
 *
 * ## Why this extends the base schema instead of replacing it
 *
 * The breadcrumb `Link` passes the whole `search` object through in BOTH
 * directions, so the reader lands back on the console exactly where they left
 * it. That needs this schema to keep every console param. The console
 * (`analytics/index.tsx`) keeps the base schema untouched, and because that is
 * a plain `z.object` it STRIPS unknown keys, so the forecasts-only params
 * below drop out of the console's URL on the trip back by themselves.
 *
 * ## THE TAB PARAM IS `list`, NEVER `tab`
 *
 * `tab` is the console's enum (`overview | planners | specialists`). Because the breadcrumb hands the console this
 * page's whole `search`, a forecasts `tab=scheduled` would land on the console,
 * fail that enum, be `.catch(undefined)`'d, and silently lose the console's tab.
 * Do not rename `list` to `tab` to "match the UI". `forecasts-search-schema.spec`
 * guards it.
 *
 * ## Conventions
 *
 * - Every field is `.optional().catch(undefined)`, matching the base: a
 *   hand-edited or stale URL degrades to the default rather than throwing.
 * - Multi-selects are comma-joined STRINGS, never arrays (see
 *   `lib/csv-search-param`): one string keeps identities stable. The schema
 *   normalises each one to a clean CSV (known members only, de-duplicated),
 *   dropping unknown members instead of rejecting the whole param, and an empty
 *   result becomes `undefined` so the param leaves the URL.
 * - Vocabularies are derived from the source arrays the page already renders
 *   from, so adding a tab, group, goal or pill cannot drift from the schema.
 * - None of these reaches the forecast queue's query key: they are client-side
 *   row filters and must stay that way. The exceptions are the Needs Cleaned
 *   server params (`cleaning`, `since`, the pickers, `q`), which reach that
 *   tab's own query instead.
 */

/**
 * Every value `list` can take: the eight work lists, plus `reference`, which
 * is a tab in the strip but deliberately not in {@link LIST_TABS} (it is not a
 * work list; see that array's docblock).
 */
type ListParamValue = ForecastListTabValue | 'reference'

const listParamValues: Array<ListParamValue> = [
  ...LIST_TABS.map((tab) => tab.value),
  'reference',
]

export const FORECAST_LIST_PARAM_VALUES = listParamValues as [
  ListParamValue,
  ...Array<ListParamValue>,
]

export type ForecastListParam = (typeof FORECAST_LIST_PARAM_VALUES)[number]

/** The list the page opens on when `list` is absent. Not written to the URL. */
export const DEFAULT_FORECAST_LIST: ForecastListParam = 'urgent'

/**
 * The `list` param value for `tab`: undefined for the default list (so it
 * leaves the URL) and for anything that is not a list. Total over `string`
 * because the tab strip hands the page a plain string.
 */
export function listParamFor(tab: string): ForecastListParam | undefined {
  const known: ReadonlyArray<string> = FORECAST_LIST_PARAM_VALUES
  if (!known.includes(tab) || tab === DEFAULT_FORECAST_LIST) return undefined
  return tab as ForecastListParam
}

const SELECTION_VIEW_PARAM_VALUES = [...SELECTION_VIEW_TABS] as [
  SelectionViewTab,
  ...Array<SelectionViewTab>,
]

/** Every vocabulary a CSV param is validated against. */
export const FORECAST_GOAL_KEYS = FORECAST_GOALS.map((goal) => goal.key)
export const FORECAST_STATUS_KEYS = FORECAST_STATUS_PILLS.map(
  (pill) => pill.key,
)
export const FORECAST_STAGE_SLUGS = CASE_DESIGN_STAGES.map(
  (stage) => stage.slug,
)

/** Stage ids for a `stage` param (slugs); unknown slugs are dropped. */
export function stageIdsFromParam(
  raw: string | undefined | null,
): Array<string> {
  return parseKnownCsv(raw, FORECAST_STAGE_SLUGS).flatMap((slug) => {
    const stage = CASE_DESIGN_STAGES.find(
      (candidate) => candidate.slug === slug,
    )
    return stage ? [stage.id] : []
  })
}

/** The `stage` param for stage ids; undefined when none, so it leaves the URL. */
export function stageParamFromIds(
  ids: ReadonlyArray<string>,
): string | undefined {
  return serializeCsvParam(
    ids.flatMap((id) => {
      const stage = CASE_DESIGN_STAGES.find((candidate) => candidate.id === id)
      return stage ? [stage.slug] : []
    }),
  )
}
export const SCHEDULED_GROUP_KEYS = SCHEDULED_GROUPS.map((group) => group.key)
export const UNSCHEDULED_GROUP_KEYS = UNSCHEDULED_GROUPS.map(
  (group) => group.key,
)
export const PLANNER_GROUP_KEYS = PLANNER_GROUPS.map((group) => group.key)
export const FORECAST_CLEANING_KINDS = CLEANING_FINDING_KINDS
/**
 * `ANNUAL_REVIEW` is not offered by the Work kind control (the Annual Review
 * switch owns it, and selecting only it is guaranteed empty), so a URL cannot
 * select it either.
 */
export const FORECAST_WORK_KINDS = CASE_WORK_KINDS.filter(
  (kind) => kind !== 'ANNUAL_REVIEW',
)
export const FORECAST_UNSCHEDULED_REASONS = Object.keys(
  UNSCHEDULED_REASON_LABELS,
) as Array<Exclude<UnscheduledReason, null>>

/**
 * `raw` as a list of members of `known`, in `raw`'s order, unknown members
 * dropped. The page reads every CSV param through this so the arrays it hands
 * to hooks and predicates are typed to the vocabulary.
 */
export function parseKnownCsv<TKey extends string>(
  raw: string | undefined | null,
  known: ReadonlyArray<TKey>,
): Array<TKey> {
  const allowed: ReadonlyArray<string> = known
  return parseCsvParam(raw).filter((value): value is TKey =>
    allowed.includes(value),
  )
}

/** A CSV param validated against `known`; unknown members are dropped. */
function knownCsv(known: ReadonlyArray<string>) {
  return z
    .string()
    .transform((raw) => serializeCsvParam(parseKnownCsv(raw, known)))
    .optional()
    .catch(undefined)
}

/** A CSV param with no closed vocabulary (user ids). */
const openCsv = z
  .string()
  .transform((raw) => serializeCsvParam(parseCsvParam(raw)))
  .optional()
  .catch(undefined)

/**
 * A switch: present as `'on'` when on, absent when off.
 *
 * **Never `'1'`.** The router's default search serializer JSON-quotes any
 * string that would parse as JSON, to keep its string-ness on the way back, so
 * `all=1` would be written `all=%221%22`. `'on'` is not JSON, so it is emitted
 * bare. Same reason no closed-vocabulary value here may be a bare number, and
 * why stages travel as slugs. (`q` is free text and can still be quoted, e.g.
 * searching `123` gives `q=%22123%22`; that round-trips and is not a bug.) Do
 * not fix this in the router's global `stringifySearch`: it is app-wide.
 */
const flag = z.literal('on').optional().catch(undefined)

export const forecastsSearchSchema = analyticsSearchSchema.extend({
  /** Which list is open. NOT `tab`: see the docblock above. */
  list: z.enum(FORECAST_LIST_PARAM_VALUES).optional().catch(undefined),
  /** On the All segment (the selection view). `'on'` when on. */
  all: flag,
  /** The tab the selection view is narrowed to; absent means All. */
  narrow: z.enum(SELECTION_VIEW_PARAM_VALUES).optional().catch(undefined),
  /** Goal shortfalls selected. CSV of `ForecastGoalKey`. */
  goals: knownCsv(FORECAST_GOAL_KEYS),
  /** Status cards selected. CSV of `ForecastStatusPillKey`. */
  status: knownCsv(FORECAST_STATUS_KEYS),
  /** Stage pills selected. CSV of stage SLUGS (ids on the wire, slugs in the URL). */
  stage: knownCsv(FORECAST_STAGE_SLUGS),
  /** Group chips per work tab. */
  schedGroups: knownCsv(SCHEDULED_GROUP_KEYS),
  unschedGroups: knownCsv(UNSCHEDULED_GROUP_KEYS),
  plannerGroups: knownCsv(PLANNER_GROUP_KEYS),
  /** Needs Cleaned problem kinds. */
  cleaning: knownCsv(FORECAST_CLEANING_KINDS),
  /**
   * Needs Cleaned meeting-date cutoff, `YYYY-MM-DD` (Denver calendar date).
   * Not JSON-parseable (the hyphens), so the router emits it bare.
   *
   * Absent means the default, 1 January of the current year, computed at
   * render and not written to the URL. `'all'` means no cutoff (the full
   * audit); it is explicit so it is shareable, and bare for the same reason
   * the flags use `'on'`. A malformed or impossible date degrades to absent,
   * i.e. the default, not to `'all'`. A SERVER param, unlike the rest of this
   * schema.
   */
  since: z
    .string()
    .refine((value) => value === CLEANING_SINCE_ALL || isCleaningSince(value))
    .optional()
    .catch(undefined),
  /** `No meeting booked` reasons. */
  reasons: knownCsv(FORECAST_UNSCHEDULED_REASONS),
  /** Work kinds. */
  work: knownCsv(FORECAST_WORK_KINDS),
  /**
   * CSV of planner user ids. Distinct from the base schema's `person`, which
   * means the OWNER (specialist): the two are different jobs and must never
   * share a field.
   */
  planner: openCsv,
  /** The Alerts view is on. `'on'` when on. */
  alerts: flag,
  /** Include Annual Reviews. `'on'` when on. */
  annual: flag,
  /** Search text. */
  q: z
    .string()
    .transform((raw) => (raw.length > 0 ? raw : undefined))
    .optional()
    .catch(undefined),
})

export type ForecastsSearch = z.infer<typeof forecastsSearchSchema>
